import { Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { AlertDiamondFilled } from '@recruit-robin/robin-theme/web-icons';
import { IndeedCreditsSyncPill } from 'components/ui/organisms/OutreachPanel/PanelHeader/PanelHeaderSubtitle/IndeedCreditsSyncPill';
import { useContext } from 'react';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type { ReactNode } from 'react';
import type { IndeedProject } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedProjectListQuery';

import { ConnectToVacancyDropdown } from 'components/ui/molecules/ConnectToVacancyDropdown/ConnectToVacancyDropdown';
import { VacancyListTab } from 'components/ui/molecules/VacancyList/VacancyList.types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useIndeedConfig } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedConfig';
import { useStyles } from './ActivateRobinJrDialog.styles';
import { IndeedProjectSelect } from './IndeedProjectSelect';
import { getHasNoIndeedCredits, getIndeedActivationState } from './IndeedActivationSection.utils';

type IndeedActivationSectionProps = {
  isProcessingVacancy: boolean;
  selectedIndeedProject: IndeedProject | null;
  selectedVacancy: Vacancy | null;
  onEditVacancy: (vacancy: Vacancy) => void;
  setSelectedIndeedProject: (project: IndeedProject | null) => void;
  setSelectedVacancy: (vacancy: Vacancy | null) => void;
};

const InlineError = ({ children }: { children: ReactNode }) => {
  const classes = useStyles({});

  return (
    <div css={classes.indeedInlineError}>
      <AlertDiamondFilled size={14} color={(c) => c.tertiary.red[50]} />
      <Typography variant={(v) => v.body.short} color={(c) => c.tertiary.red[50]}>
        {children}
      </Typography>
    </div>
  );
};

export const IndeedActivationSection = ({
  isProcessingVacancy,
  onEditVacancy,
  selectedIndeedProject,
  selectedVacancy,
  setSelectedIndeedProject,
  setSelectedVacancy,
}: IndeedActivationSectionProps) => {
  const classes = useStyles({});
  const { dictionary } = useContext(LocalizationContext);
  const indeedConfig = useIndeedConfig();
  const hasNoIndeedCredits = getHasNoIndeedCredits(indeedConfig) && !indeedConfig.isIndeedCreditsOutOfSync;
  const { shouldShowQualityError } = getIndeedActivationState({
    isLoading: false,
    isProcessingVacancy,
    selectedIndeedProject,
    selectedVacancy,
  });
  const qualityErrorMessage = shouldShowQualityError ? dictionary.indeedJobNotHighQuality : undefined;

  return (
    <>
      <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
        {dictionary.activateRobinJrIndeedDescription}
      </Typography>

      <div css={classes.indeedCreditsSection}>
        <div css={classes.indeedCreditsRow}>
          <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
            {dictionary.indeedCreditsRemainingPrefix}
          </Typography>
          {indeedConfig.isIndeedCreditsOutOfSync ? (
            <IndeedCreditsSyncPill creditBalance={indeedConfig.creditBalance} />
          ) : (
            <Pill
              label={dictionary.indeedCreditsLabel(indeedConfig.creditBalance)}
              style="icon-removable"
              type="default"
              size="small"
              onClick={() => undefined}
              padding="text"
            />
          )}
          <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
            {dictionary.indeedCreditsRemainingSuffix}
          </Typography>
        </div>

        {hasNoIndeedCredits && <InlineError>{dictionary.indeedNoCredits}</InlineError>}
      </div>

      <div css={classes.indeedPickers}>
        <div css={classes.indeedJobPicker}>
          <ConnectToVacancyDropdown
            selectedVacancy={selectedVacancy}
            onSelectVacancy={setSelectedVacancy}
            enabledTabs={[VacancyListTab.IndeedJobs, VacancyListTab.AtsJobs]}
            initialTab={VacancyListTab.IndeedJobs}
            onlyAtsVacancies={false}
            variant="gray"
            contained
            isProcessingVacancy={isProcessingVacancy}
            error={qualityErrorMessage}
          />

          {shouldShowQualityError && (
            <InlineError>
              {dictionary.indeedJobNotHighQuality}{' '}
              <button
                type="button"
                css={classes.indeedQualityErrorLink}
                onClick={() => selectedVacancy && onEditVacancy(selectedVacancy)}>
                {dictionary.editAndEnhanceJob}
              </button>{' '}
              {dictionary.toActivateRobinJr}
            </InlineError>
          )}
        </div>

        <IndeedProjectSelect value={selectedIndeedProject} onChange={setSelectedIndeedProject} />
      </div>
    </>
  );
};
